import React from "react";
import { getPlatformConfig } from "@/config/platforms";

/**
 * Metrics for a single platform, as returned by the backend's
 * `post.metrics_by_platform`. All numeric fields are nullable because
 * Meta only surfaces a subset per (platform, post_type) — FB feed
 * insights don't carry comments/shares, IG feeds don't carry
 * link_clicks, etc. The component renders missing cells as em-dashes
 * rather than zeros so the user can distinguish "no engagement" from
 * "no data captured".
 */
export interface PlatformMetrics {
  reactions?: number | null;
  likes?: number | null;
  comments?: number | null;
  shares?: number | null;
  saved?: number | null;
  views?: number | null;
  reach?: number | null;
  impressions?: number | null;
  engagement?: number | null;
  last_fetched_at?: string | null;
}

export type MetricsByPlatform = Partial<Record<string, PlatformMetrics>>;

export type StatsLocale = "en" | "it";

interface PublishedPostStatsProps {
  metricsByPlatform: MetricsByPlatform | null | undefined;
  /** Optional permalink per platform (FB/IG/TikTok). When provided,
   *  the platform badge in that row becomes an external link to the
   *  live post. Lets a single card surface "view on FB" + "view on IG"
   *  without needing top-right corner badges. */
  permalinks?: Partial<Record<string, string | null | undefined>>;
  /** Copy locale. Defaults to "en". Client portal passes "it" so the
   *  column headers read in Italian. */
  locale?: StatsLocale;
  /** Paid impressions delivered against this post (its campaigns'
   *  snapshots). When > 0 the panel adds an organic-vs-paid split bar —
   *  organic = platform impressions minus paid, the same model as the
   *  report Overview and the idea Impact panel. */
  paidImpressions?: number;
  className?: string;
}

const COPY: Record<StatsLocale, {
  columns: Record<MetricColumnKey, { shortLabel: string; fullLabel: string }>;
  split: { title: string; organic: string; paid: string };
}> = {
  en: {
    columns: {
      views: { shortLabel: "Views", fullLabel: "Total views" },
      reach: { shortLabel: "Reach", fullLabel: "Unique accounts reached" },
      likes: { shortLabel: "Likes", fullLabel: "Likes / reactions" },
      comments: { shortLabel: "Comm.", fullLabel: "Comments" },
      shares: { shortLabel: "Shares", fullLabel: "Shares" },
    },
    split: { title: "Organic vs paid", organic: "Organic", paid: "Paid" },
  },
  it: {
    columns: {
      views: { shortLabel: "Visual.", fullLabel: "Visualizzazioni totali" },
      reach: { shortLabel: "Copert.", fullLabel: "Copertura unica" },
      likes: { shortLabel: "Like", fullLabel: "Like e reazioni" },
      comments: { shortLabel: "Comm.", fullLabel: "Commenti" },
      shares: { shortLabel: "Cond.", fullLabel: "Condivisioni" },
    },
    split: { title: "Organico vs paid", organic: "Organico", paid: "Paid" },
  },
};

/**
 * Per-platform stats panel rendered below the media on a published
 * post card. Column-aligned grid where rows are platforms and
 * columns are core engagement metrics. Same column positions across
 * every platform so the eye tracks down the column to compare FB vs
 * IG without re-anchoring.
 *
 * Columns are dropped only when no platform has data for that
 * metric — that way IG-only posts collapse to a tight 5-col grid
 * without empty filler, but mixed FB+IG cards keep consistent
 * alignment even when FB's row is sparse.
 */
export const PublishedPostStats: React.FC<PublishedPostStatsProps> = ({
  metricsByPlatform,
  permalinks,
  locale = "en",
  paidImpressions = 0,
  className,
}) => {
  const copy = COPY[locale];
  // Treat null/undefined/empty the same — caller (EditorPostCard,
  // TopPostsSection) gates on whether the panel is contextually
  // appropriate (post is published / appears in the top list).
  // Within the panel, we always render the same skeleton so cards
  // stay aligned regardless of whether data exists yet.
  const data: MetricsByPlatform = metricsByPlatform ?? {};

  // Detect which platforms actually have data — drives the active
  // column set so columns with no data anywhere drop out.
  const dataPlatforms = (
    ["facebook", "instagram", "tiktok"] as const
  ).filter((k) => {
    const m = data[k];
    return m != null && hasAnyMetric(m);
  });

  // Rendered rows: always include FB + IG so card heights stay
  // consistent across the grid (an IG-only post still shows an FB
  // row with em-dashes). TikTok is opt-in — only rendered when it
  // has data, since most accounts here don't publish to TikTok and
  // we don't want to bloat every card with a permanent empty row.
  type Entry = { key: string; metrics: PlatformMetrics };
  const renderKeys: string[] = ["facebook", "instagram"];
  if (data.tiktok && hasAnyMetric(data.tiktok)) {
    renderKeys.push("tiktok");
  }
  const ordered: Entry[] = renderKeys.map((key) => ({
    key,
    metrics: data[key] ?? {},
  }));

  // Decide which columns to render. A column survives only if at
  // least one platform with data has a non-null value for it.
  // Special case: when no platform has data at all, render the
  // full default column set so the empty panel still has a
  // recognizable skeleton instead of collapsing to nothing.
  const activeColumns =
    dataPlatforms.length === 0
      ? METRIC_COLUMNS
      : METRIC_COLUMNS.filter((col) =>
          dataPlatforms.some(
            (k) => readCell(k, data[k] ?? {}, col.key) != null,
          ),
        );

  return (
    <div
      className={[
        "mt-3 rounded-xl bg-linear-to-br from-gray-50 to-white",
        "ring-1 ring-gray-200/70 shadow-[0_1px_2px_-1px_rgb(0_0_0/0.04)]",
        "px-3.5 pt-3 pb-3",
        className ?? "",
      ].join(" ")}
    >
      {/* Metric grid. CSS grid with `auto` for the platform badge and
          flex-1 columns for every active metric. `tabular-nums` plus
          right-alignment means digits line up cleanly across rows. */}
      <div
        className="grid items-center gap-x-2 gap-y-1.5"
        style={{
          gridTemplateColumns: `auto repeat(${activeColumns.length}, minmax(0, 1fr))`,
        }}
      >
        {/* Header row: empty cell for the badge column, then a small
            uppercase label per metric column. */}
        <span />
        {activeColumns.map((col) => {
          const l = copy.columns[col.key];
          return (
            <span
              key={col.key}
              className="text-right text-[9px] font-semibold uppercase tracking-wider text-gray-400"
              title={l.fullLabel}
            >
              {l.shortLabel}
            </span>
          );
        })}

        {/* Platform rows */}
        {ordered.map(({ key, metrics }) => {
          const config = getPlatformConfig(key);
          const Icon = config?.iconComponent;
          // A row is "empty" when no metric on this platform has a
          // value. Dim the whole row so the user reads it as a
          // placeholder for an unpublished/uncovered channel rather
          // than thinking the post performed poorly there.
          const isEmpty = !hasAnyMetric(metrics);
          const permalink = permalinks?.[key];
          const badgeClasses = [
            "inline-flex h-5 w-5 shrink-0 items-center justify-center",
            "rounded-md shadow-[0_1px_1px_rgb(0_0_0/0.06)] transition-transform",
            config?.iconBgClass ?? "bg-gray-200",
            config?.iconTextClass ?? "text-gray-700",
            isEmpty ? "opacity-40 grayscale" : "",
            // Only treat as interactive when there's actually a link.
            permalink && !isEmpty ? "hover:scale-110 cursor-pointer" : "",
          ].join(" ");
          const badgeTitle = isEmpty
            ? `No data for ${config?.label}`
            : permalink
              ? `Open on ${config?.label}`
              : config?.label;
          return (
            <React.Fragment key={key}>
              {Icon ? (
                permalink && !isEmpty ? (
                  <a
                    href={permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={badgeClasses}
                    title={badgeTitle}
                    aria-label={badgeTitle}
                  >
                    <Icon size={11} />
                  </a>
                ) : (
                  <span
                    className={badgeClasses}
                    title={badgeTitle}
                    aria-label={config?.label}
                  >
                    <Icon size={11} />
                  </span>
                )
              ) : (
                <span />
              )}
              {activeColumns.map((col) => {
                const value = readCell(key, metrics, col.key);
                return (
                  <span
                    key={col.key}
                    className={[
                      "text-right text-[11px] tabular-nums leading-snug",
                      value == null
                        ? "text-gray-300"
                        : "font-semibold text-gray-900",
                    ].join(" ")}
                  >
                    {value == null ? "—" : formatNumber(value)}
                  </span>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      {(() => {
        // Organic vs paid split — only when the post was boosted and we
        // have delivery to split against. IG's API replaced impressions
        // with views (v22), so snapshots often carry views only — fall
        // back per platform.
        const totalImpressions = Object.values(data).reduce(
          (sum, m) => sum + (m?.impressions ?? m?.views ?? 0),
          0,
        );
        if (paidImpressions <= 0 || totalImpressions <= 0) return null;
        const organic = Math.max(0, totalImpressions - paidImpressions);
        const organicPct = Math.round((organic / totalImpressions) * 100);
        return (
          <div className="mt-2.5 pt-2.5 border-t border-gray-200/70">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
              {copy.split.title}
            </p>
            <div className="h-1.5 w-full rounded-full overflow-hidden bg-gray-200 flex">
              <div
                className="bg-blue-500 h-full"
                style={{ width: `${organicPct}%` }}
              />
              <div
                className="bg-purple-400 h-full"
                style={{ width: `${100 - organicPct}%` }}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-[10px] text-gray-600 tabular-nums">
              <span>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mr-1 align-middle" />
                {copy.split.organic} {formatNumber(organic)} · {organicPct}%
              </span>
              <span>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-400 mr-1 align-middle" />
                {copy.split.paid} {formatNumber(paidImpressions)}
              </span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Column definitions                                                  */
/* ------------------------------------------------------------------ */

type MetricColumnKey = "views" | "reach" | "likes" | "comments" | "shares";

interface MetricColumn {
  key: MetricColumnKey;
  shortLabel: string;
  fullLabel: string;
}

/** Columns rendered in this exact order when their data exists. View
 *  count goes first because for reels it's the dominant signal;
 *  followed by reach (unique audience), then the engagement actions. */
const METRIC_COLUMNS: readonly MetricColumn[] = [
  { key: "views", shortLabel: "Views", fullLabel: "Total views" },
  { key: "reach", shortLabel: "Reach", fullLabel: "Unique accounts reached" },
  { key: "likes", shortLabel: "Likes", fullLabel: "Likes / reactions" },
  { key: "comments", shortLabel: "Comm.", fullLabel: "Comments" },
  { key: "shares", shortLabel: "Shares", fullLabel: "Shares" },
];

/** Map a (platform, column) pair to the right field on the metrics
 *  payload. The column abstraction lets us put FB reactions and IG
 *  likes in the same column without lying about the semantics — the
 *  user reads them as "primary engagement count" either way. */
function readCell(
  platformKey: string,
  metrics: PlatformMetrics,
  columnKey: MetricColumnKey,
): number | null | undefined {
  if (columnKey === "likes" && platformKey === "facebook") {
    return metrics.reactions;
  }
  return metrics[columnKey];
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function hasAnyMetric(m: PlatformMetrics): boolean {
  return (
    m.reactions != null ||
    m.likes != null ||
    m.comments != null ||
    m.shares != null ||
    m.saved != null ||
    m.views != null ||
    m.reach != null ||
    m.impressions != null ||
    m.engagement != null
  );
}

/** Compact number formatter: 1234 -> "1,234", 12345 -> "12.3k",
 *  1234567 -> "1.2M". Tuned for ~3 chars to keep grid columns narrow. */
function formatNumber(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}
